import { useState, useEffect, useCallback } from 'react';
import { Mail, Plus, Edit, Trash2, CheckCircle, Calendar, Download, Upload, AlertTriangle } from 'lucide-react';
import Card from '../components/common/Card';
import Table from '../components/common/Table';
import SearchBar from '../components/common/SearchBar';
import Pagination from '../components/common/Pagination';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';
import Button from '../components/common/Button';
import Toast from '../components/common/Toast';
import AddEmailModal from '../components/features/EmailManagement/AddEmailModal';
import EditEmailModal from '../components/features/EmailManagement/EditEmailModal';
import ImportEmailsModal from '../components/features/EmailManagement/ImportEmailsModal';
import { getEmails, deleteEmail } from '../services/emailServiceSwitch';
import { getDeliveries } from '../services/deliveryAuditService';
import { generateEmailCsv, downloadCsv } from '../utils/csvHelpers';
import './Emails.css';

/**
 * Emails Page Component
 * 
 * Displays list of email recipients with verification status, search, filter, and management actions
 */
export default function Emails() {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  
  // Search and pagination state
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);

  // Toast notification state
  const [toast, setToast] = useState(null);

  // Bounce data from delivery audit (keyed by email address)
  const [bounceMap, setBounceMap] = useState({});

  // Fetch emails function (defined before useEffect)
  const fetchEmails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        search: searchTerm,
        page: currentPage,
        limit: itemsPerPage
      };

      const response = await getEmails(params);
      setEmails(response.data);
      setTotalPages(response.pagination.totalPages);
      if (response.summary) {
        setSummary(response.summary);
      }
    } catch (err) {
      setError(err.message || 'Failed to load emails');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, currentPage]);

  // Fetch emails on mount and when dependencies change
  useEffect(() => {
    fetchEmails();
  }, [fetchEmails]);

  // Fetch bounce/complaint data from delivery audit (once on mount)
  useEffect(() => {
    const fetchBounceData = async () => {
      try {
        // Fetch all bounced + complained deliveries to build a complete map
        const [bouncedRes, complainedRes] = await Promise.all([
          getDeliveries({ status: 'bounced', pageSize: 200, daysBack: 90 }),
          getDeliveries({ status: 'complained', pageSize: 200, daysBack: 90 })
        ]);
        const map = {};
        for (const d of (bouncedRes.deliveries || [])) {
          const email = d.recipient_email;
          if (!email) continue;
          if (!map[email]) map[email] = { bounced: 0, complained: 0 };
          map[email].bounced++;
        }
        for (const d of (complainedRes.deliveries || [])) {
          const email = d.recipient_email;
          if (!email) continue;
          if (!map[email]) map[email] = { bounced: 0, complained: 0 };
          map[email].complained++;
        }
        setBounceMap(map);
      } catch {
        // Non-critical — just won't show bounce badges
      }
    };
    fetchBounceData();
  }, []);

  // Handle search
  const handleSearch = (e) => {
    const value = typeof e === 'string' ? e : e.target.value;
    setSearchTerm(value);
    setCurrentPage(1);
  };

  // Verification status indicator — all emails are now Active (PSE-299)
  const getStatusIndicator = () => {
    return (
      <span className="status-badge status-verified">
        <CheckCircle size={16} />
        Active
      </span>
    );
  };

  // Table columns
  const columns = [
    {
      key: 'email',
      label: 'Email Address',
      sortable: false,
      render: (email) => {
        const bounce = bounceMap[email.email];
        return (
          <div className="email-cell">
            <Mail size={18} />
            <span>{email.email}</span>
            {bounce && (
              <span className="bounce-badge" title={`${bounce.bounced} bounce(s), ${bounce.complained} complaint(s)`}>
                <AlertTriangle size={14} />
                {bounce.bounced + bounce.complained}
              </span>
            )}
          </div>
        );
      }
    },
    {
      key: 'name',
      label: 'Name',
      sortable: false
    },
    {
      key: 'verificationStatus',
      label: 'Status',
      sortable: false,
      render: () => getStatusIndicator()
    },
    {
      key: 'dateAdded',
      label: 'Date Added',
      sortable: false,
      render: (email) => (
        <div className="date-cell">
          <Calendar size={16} />
          <span>{new Date(email.dateAdded).toLocaleDateString()}</span>
        </div>
      )
    },
    {
      key: 'lastAttempt',
      label: 'Last Attempt',
      sortable: false,
      render: (email) => (
        <div className="date-cell">
          <span>{new Date(email.lastAttempt).toLocaleDateString()}</span>
        </div>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      render: (email) => (
        <div className="action-buttons">
          <button
            className="action-btn edit-btn"
            onClick={() => handleEditEmail(email)}
            title="Edit email"
          >
            <Edit size={16} />
          </button>
          <button
            className="action-btn delete-btn"
            onClick={() => handleDeleteEmail(email)}
            title="Delete email"
          >
            <Trash2 size={16} />
          </button>
        </div>
      )
    }
  ];

  // Action handlers
  const handleEditEmail = (email) => {
    setSelectedEmail(email);
    setShowEditModal(true);
  };

  const handleDeleteEmail = async (email) => {
    if (!window.confirm(`Are you sure you want to delete this email? This action cannot be undone.`)) {
      return;
    }

    try {
      await deleteEmail(email.id);
      showToast('Email deleted successfully', 'success');
      fetchEmails();
    } catch (error) {
      showToast(error.message || 'Failed to delete email', 'error');
    }
  };

  const handleAddEmail = () => {
    setShowAddModal(true);
  };

  const handleExportCsv = async () => {
    try {
      // Fetch all emails (up to 500) respecting current filters
      const allEmails = await getEmails({
        search: searchTerm,
        page: 1,
        limit: 500
      });
      const csv = generateEmailCsv(allEmails.data);
      downloadCsv(csv, `emails-export-${new Date().toISOString().slice(0, 10)}.csv`);
      showToast(`Exported ${allEmails.data.length} email(s)`, 'success');
    } catch (err) {
      showToast(err.message || 'Failed to export emails', 'error');
    }
  };

  // Toast helper
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  // Handle successful operations
  const handleSuccess = (message) => {
    showToast(message, 'success');
    // Refresh email list
    fetchEmails();
  };

  // Calculate stats from API summary (all emails) or fall back to current page
  const bouncedCount = emails.filter(e => bounceMap[e.email]).length;
  const stats = summary ? {
    total: summary.total,
    active: summary.total,
    bounced: bouncedCount
  } : {
    total: emails.length || 0,
    active: emails.length || 0,
    bounced: bouncedCount
  };

  return (
    <div className="emails-page">
      <div className="page-header">
        <div className="header-content">
          <div className="header-text">
            <h1 className="page-title">
              <Mail size={32} />
              Email Recipients
            </h1>
            <p className="page-subtitle">Manage email recipients</p>
          </div>
          <div className="header-actions">
            <Button
              variant="secondary"
              onClick={handleExportCsv}
              icon={<Download size={20} />}
            >
              Export CSV
            </Button>
            <Button
              variant="secondary"
              onClick={() => setShowImportModal(true)}
              icon={<Upload size={20} />}
            >
              Import CSV
            </Button>
            <Button
              variant="primary"
              onClick={handleAddEmail}
              icon={<Plus size={20} />}
            >
              Add Email
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {!loading && !error && (
        <div className="email-stats">
          <Card className="stat-card">
            <div className="stat-content">
              <div className="stat-icon stat-total">
                <Mail size={24} />
              </div>
              <div className="stat-info">
                <div className="stat-value">{stats.total}</div>
                <div className="stat-label">Total Emails</div>
              </div>
            </div>
          </Card>
          <Card className="stat-card">
            <div className="stat-content">
              <div className="stat-icon stat-verified">
                <CheckCircle size={24} />
              </div>
              <div className="stat-info">
                <div className="stat-value">{stats.active}</div>
                <div className="stat-label">Active</div>
              </div>
            </div>
          </Card>
          {stats.bounced > 0 && (
            <Card className="stat-card">
              <div className="stat-content">
                <div className="stat-icon stat-bounced">
                  <AlertTriangle size={24} />
                </div>
                <div className="stat-info">
                  <div className="stat-value">{stats.bounced}</div>
                  <div className="stat-label">Bounced</div>
                </div>
              </div>
            </Card>
          )}
        </div>
      )}

      <Card className="emails-card">
        {/* Search Bar */}
        <div className="emails-controls">
          <SearchBar
            value={searchTerm}
            onChange={handleSearch}
            placeholder="Search emails by address or name..."
          />
        </div>

        {/* Loading State */}
        {loading && <LoadingSpinner />}

        {/* Error State */}
        {error && (
          <ErrorMessage
            message={error}
            onRetry={() => window.location.reload()}
          />
        )}

        {/* Emails Table */}
        {!loading && !error && (
          <>
            <Table
              columns={columns}
              data={emails}
              emptyMessage="No emails found"
            />

            {/* Pagination */}
            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            )}
          </>
        )}
      </Card>

      {/* Modals */}
      <AddEmailModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={handleSuccess}
      />

      <EditEmailModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedEmail(null);
        }}
        email={selectedEmail}
        onSuccess={handleSuccess}
      />

      <ImportEmailsModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onSuccess={handleSuccess}
      />

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={5000}
        />
      )}
    </div>
  );
}
